/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  TextField,
} from '@mui/material';

import useSetState from 'hooks/useSetState';
import GoogleMapPlaces from '../GoogleMapPlaces';

const defaultState = {
  address: '',
  addressState: '',
  error: '',
  inputManually: false,
  placeId: '',
  postcode: '',
  suburb: '',
};

export const AddressField = (props) => {
  const {
    callbacks: { toggleSetValue },
    currentAddress,
  } = props;

  const [state, setState] = useSetState(defaultState);
  const { address, addressState, error, inputManually, placeId, postcode, suburb } = state;

  const states = useSelector((state) => state.properties.states.value);

  useEffect(() => {
    setState({
      address: currentAddress?.value?.address || '',
      addressState: currentAddress?.value?.state || '',
      inputManually: (currentAddress?.value?.postcode && !currentAddress?.value?.place_id) || inputManually,
      placeId: currentAddress?.value?.place_id || '',
      postcode: currentAddress?.value?.postcode || '',
      suburb: currentAddress?.value?.suburb || '',
    });
  }, [currentAddress]);

  const updateAddress = () => {
    let updatedAddress = {
      address: address,
      suburb: suburb,
      state: addressState,
      postcode: postcode,
    };

    const errorMessage = !Object.values(updatedAddress).every((x) => [null, ''].includes(x))
      ? Object.values(updatedAddress).some((x) => [null, ''].includes(x))
        ? 'Address is not valid'
        : ''
      : '';

    const isEmpty = Object.values(updatedAddress).every((x) => [null, ''].includes(x));

    setState({ error: errorMessage });

    if (placeId) updatedAddress = { ...updatedAddress, place_id: placeId };

    toggleSetValue(updatedAddress, errorMessage, isEmpty);
  };

  const fillAddress = (value, details) => {
    if (!value) {
      setState(defaultState);
      return;
    }

    const newAddressState = {
      ...defaultState,
      address: value?.structured_formatting?.main_text || '',
      placeId: value?.place_id || '',
      inputManually: false,
    };

    if (details?.address_components) {
      for (const component of details.address_components) {
        const componentType = component.types[0];
        switch (componentType) {
          case 'postal_code':
            newAddressState.postcode = component.long_name;
            break;
          case 'locality':
            newAddressState.suburb = component.long_name;
            break;
          case 'administrative_area_level_1':
            newAddressState.addressState = component.short_name;
            break;
          default:
            break;
        }
      }
    }

    setState(newAddressState);
  };

  useEffect(() => {
    const debouncedUpdateValue = debounce(updateAddress, 500);

    debouncedUpdateValue();

    return () => debouncedUpdateValue.cancel();
  }, [address, suburb, addressState, postcode, placeId]);

  return (
    <Box noValidate>
      {inputManually ? (
        <Stack spacing={1.5}>
          <TextField
            onChange={(e) => setState({ address: e.target.value, placeId: '' })}
            label="Address"
            value={address}
            size="small"
            required
          />
          <TextField
            onChange={(e) => setState({ suburb: e.target.value, placeId: '' })}
            label="Suburb"
            value={suburb}
            size="small"
            required
          />
          <Stack
            direction="row"
            spacing={1}
          >
            <FormControl fullWidth>
              <Select
                defaultValue=""
                displayEmpty
                onChange={(e) => setState({ addressState: e.target.value, placeId: '' })}
                value={addressState}
                variant="outlined"
                size="small"
              >
                <MenuItem value="">Select...</MenuItem>
                {Object.values(states).map((addState) => (
                  <MenuItem
                    key={addState.value}
                    value={addState.value}
                  >
                    {addState.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              onChange={(e) => setState({ postcode: e.target.value, placeId: '' })}
              label="Postcode"
              value={postcode}
              size="small"
              fullWidth
              required
            />
          </Stack>
        </Stack>
      ) : (
        <GoogleMapPlaces
          onAddressChange={fillAddress}
          selectedValue={state}
        />
      )}
      {error ? (
        <Typography
          variant="caption"
          color="error"
          padding={0.5}
        >
          {error}
        </Typography>
      ) : null}
      <FormControlLabel
        value="end"
        control={
          <Checkbox
            checked={inputManually}
            size="small"
            onChange={() => {
              const togglingToManual = !inputManually;

              if (error) {
                setState({
                  ...defaultState,
                  inputManually: togglingToManual,
                  error: '',
                });
              } else {
                setState({
                  inputManually: togglingToManual,
                });
              }
            }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{ fontSize: '0.9rem' }}
          >
            Input Address Manually
          </Typography>
        }
        labelPlacement="end"
        sx={{ pt: 1, pl: 1 }}
      />
    </Box>
  );
};
